import React, { createContext, useState, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import {
  prefsToConsentParams,
  isRegulatedRegion,
  getCachedGeo,
  setCachedGeo,
  readStoredConsent,
  persistConsent,
} from '../helpers/consentMode'

/**
 * @typedef {Object} ConsentPrefs
 * @property {boolean} analytics
 * @property {boolean} marketing
 * @property {boolean} advertising
 */

/** @type {ConsentPrefs} */
export const COOKIE_CATEGORIES = [
  {
    key: 'necessary',
    label: 'Necessary',
    description: 'Required for basic site functionality (always enabled).',
    required: true,
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'Helps us understand site usage & performance.',
    required: false,
  },
  {
    key: 'marketing',
    label: 'Marketing',
    description: 'Enables personalized marketing & tracking.',
    required: false,
  },
  {
    key: 'advertising',
    label: 'Advertising',
    description: 'Used by third parties for targeted ads.',
    required: false,
  },
  {
    key: 'liveChat',
    label: 'Live Chat',
    description: 'Supports real-time chat support & transcripts.',
    required: false,
  },
]

// shape of prefs
export const defaultPrefs = COOKIE_CATEGORIES.reduce((acc, { key }) => {
  acc[key] = true
  return acc
}, { confirmed: false })

/**
 * @typedef {Object} CookieConsentContextValue
 * @property {ConsentPrefs} prefs
 * @property {(p: ConsentPrefs) => void} setPrefs
 */

/** @type {CookieConsentContextValue} */
const defaultContext = {
  prefs: defaultPrefs,
  setPrefs: () => {},
  regionEligible: false,
}

const CookieConsentContext = createContext(defaultContext)


const GEO_API = 'https://geolocation-db.com/json/d637fa90-2699-11f0-b1e4-0b6fbd2235a3'  // CORS-friendly, no key required


/**
 * Hook to read & write cookie‐consent prefs
 * @returns {CookieConsentContextValue}
 */
export const useCookieConsent = () => useContext(CookieConsentContext)

/**
 * Provides consent prefs via Context; persists to localStorage+cookie.
 * @param {{ children: React.ReactNode }} props
 */
export const CookieConsentProvider = ({ children }) => {
  const [prefs, _setPrefs] = useState(defaultPrefs)
  const [askOpen, setAskOpen] = useState(false)
  const setPrefs = newPrefs => {
    const confirmedPrefs = persistConsent(newPrefs)
    _setPrefs(confirmedPrefs)
    // Signal Google Consent Mode v2
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', prefsToConsentParams(confirmedPrefs))
    }
    if (typeof window !== 'undefined') {
      window.__thirdPartyScripts?.updateConsent(confirmedPrefs)
    }
    if (
      typeof window !== 'undefined' &&
      window.location &&
      typeof window.location.reload === 'function'
    ) {
      window.location.reload()
    }
  }
  const [regionEligible, setRegionEligible] = useState(false)

  useEffect(() => {
    const storedPrefs = readStoredConsent()
    if (storedPrefs) _setPrefs(storedPrefs)
  }, [])

  // 3. Detect region on first mount
  useEffect(() => {
    const cached = getCachedGeo()
    if (cached) {
      if (isRegulatedRegion(cached.country_code, cached.state)) {
        setRegionEligible(true)
      }
      return
    }
    fetch(GEO_API)
      .then(res => res.json())
      .then(data => {
        setCachedGeo(data)
        if (isRegulatedRegion(data.country_code, data.state)) {
          setRegionEligible(true)
        }
      })
      .catch(err => {
        setRegionEligible(true)
        console.warn('Geo lookup failed', err)
      })
  }, [])

  return (
    <CookieConsentContext.Provider value={{ prefs, setPrefs, regionEligible, askOpen, setAskOpen }}>
      {children}
    </CookieConsentContext.Provider>
  )
}
CookieConsentProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
